/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type ComponentRef, type OnDestroy, type OnInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { type Polling, type Rest, EventService, I18nService, LazyloadService, LocalStorageService, SessionStorageService, TooltipDirective, UNPLACEHOLDER, UtilService } from '@dm/core';
import { EventServiceKeyE } from 'app/common/constants/event-service-key';
import { SWITCH } from 'app/config';
import { cloneDeep } from 'lodash';
import { type Subscription } from 'rxjs';

import { TaskSComponent } from '../../task-s/task-s.component';
import { type TaskData, type TaskModel, Status, TaskCenterService } from '../task-center.service';
import { TaskStrService } from '../task-str.service';

/** 任务面板显示的任务类型 */
export enum TaskPanelStatus {
    /** 最近10个任务 */
    Recent = 'recent',
    /** 未完成任务，包括 executing,wait,rollback*/
    Unfinished = 'unfinished',
    /** 失败任务，包括fail,rollback_fail,partial_fail*/
    Failed = 'failed',
}

export type PanelTaskStatusQueryMap = Record<TaskPanelStatus, Parameters<Rest['setParams']>[0]>;

@Component({
    selector: 'app-task-panel',
    templateUrl: './task-panel.component.html',
    styleUrls: ['./task-panel.component.scss'],
})
export class TaskPanelComponent implements OnInit, OnDestroy {
    @ViewChild(TooltipDirective) tooltip: TooltipDirective;

    UNPLACEHOLDER = UNPLACEHOLDER;
    taskPanelStatus = TaskPanelStatus;
    taskStatus = Status;

    allTasks = Object.values(this.taskPanelStatus);

    /** 当前显示的任务类型 */
    currentTaskType: TaskPanelStatus = TaskPanelStatus.Recent;

    /** 任务状态颜色 */
    statusColor = this.str.statusColor;

    /** 任务状态文本 */
    statusStr = this.str.statusText;

    tooltipVisible = false;

    /** 是否所有任务都已经已读 */
    hasAllTasksRead = true;

    taskSComponent: ComponentRef<any>;

    /** 默认任务数据 */
    defaultTaskData: TaskModel = {
        isLoading: true,
        tasks: [],
        noData: true,
        hasUnreadTasks: false,
    };

    i18n = {
        [TaskPanelStatus.Failed]: this.i18nService.get('common.downloadFailed_label'),
        [TaskPanelStatus.Recent]: this.i18nService.get('monitor.recentTasks_latast_lab'),
        [TaskPanelStatus.Unfinished]: this.i18nService.get('monitor.recentTasks_undoneTask_lab'),
        title: this.i18nService.get('monitor.recentTasks_title'),
        viewAllTasks: this.i18nService.get('monitor.recentTasks_viewTasks_lab'),
        noData: this.i18nService.get('monitor.recentTasks_result_noData'),
        unreadTaskTip: this.i18nService.get('monitor.recentTasks_UnreadTask_info'),
        taskTip: this.i18nService.get('home.tasks_tip'),
    };

    /** 每种task type对应的rest请求参数 */
    panelTaskStatusQueryMap: PanelTaskStatusQueryMap = {
        [TaskPanelStatus.Recent]: {
            range: '{"offset":0,"limit":10}',
        },
        [TaskPanelStatus.Failed]: {
            filter: null,
            range: '{"offset":0,"limit":10}',
        },
        [TaskPanelStatus.Unfinished]: {
            filter: null,
        },
    };

    /** task type 对应的 task status，例如失败类型任务包括失败/回滚失败/部分失败  */
    panelTaskStatusMap: Record<TaskPanelStatus, Status[] | null> = {
        [TaskPanelStatus.Recent]: null,
        [TaskPanelStatus.Failed]: [Status.failed, Status.rollbackFailed, Status.particalFailed],
        [TaskPanelStatus.Unfinished]: [Status.running, Status.waiting, Status.rollbacking],
    };

    /** 任务轮询 Map */
    pollingMap: Record<TaskPanelStatus, Polling | null> = {
        [TaskPanelStatus.Recent]: null,
        [TaskPanelStatus.Failed]: null,
        [TaskPanelStatus.Unfinished]: null,
    };

    /** 任务面板数据 */
    taskDataMap: Record<TaskPanelStatus, TaskModel | null> = {
        [TaskPanelStatus.Recent]: cloneDeep(this.defaultTaskData),
        [TaskPanelStatus.Failed]: cloneDeep(this.defaultTaskData),
        [TaskPanelStatus.Unfinished]: cloneDeep(this.defaultTaskData),
    };

    /** 轮询对象销毁方法 */
    pollingDestroyer: () => void;

    /** 立即执行所有轮询 */
    pollingImmediate: () => void;

    /** 按时间降序排列任务 */
    private timeSort = '{"startTime":"desc"}';

    /** 轮询间隔，秒 */
    private pollingPeriod = 10;

    /** 所有租户 */
    private allVstore = -1;

    private sub$: Subscription;

    constructor(
        private i18nService: I18nService,
        private taskService: TaskCenterService,
        private eventService: EventService,
        public str: TaskStrService,
        private router: Router,
        private utilService: UtilService,
        private sessionStorageService: SessionStorageService,
        private localStorageService: LocalStorageService,
        private lazyLoadService: LazyloadService,
        private route: ActivatedRoute,
    ) { }

    get isSimpleMode(): boolean {
        return SWITCH.IS_SIMPLE_MODE;
    }

    ngOnInit(): void {
        this.tasksPollingParamsInit();
        this.subscribeToUpdatedTask();
        const { destroyer, immediate } = this.createTasksPolling();
        this.pollingDestroyer = destroyer;
        this.pollingImmediate = immediate;
    }

    ngOnDestroy(): void {
        this.pollingDestroyer();

        this.sub$ = this.router.events.subscribe((evt) => {
            if (evt instanceof NavigationEnd && this.tooltipVisible) {
                this.tooltipVisible = false;
            }
        });
    }

    /**
     * 初始化各任务类型的查询条件
     */
    tasksPollingParamsInit(): void {
        for (const [taskType, statusArr] of Object.entries(this.panelTaskStatusMap)) {
            if (statusArr) {
                const taskFilterArr = statusArr.map((status) => ({ taskStatus: status }));
                this.panelTaskStatusQueryMap[taskType].filter = JSON.stringify(taskFilterArr);
            }
            this.panelTaskStatusQueryMap[taskType].sort = this.timeSort;
        }
    }

    /**
     * 订阅外部任务事件
     */
    subscribeToUpdatedTask(): void {
        this.eventService.subscribe('updateTaskCount', () => {
            for (const [taskType, polling] of Object.entries(this.pollingMap)) {
                if (polling && !polling.isPaused) {
                    polling.immediate();
                    this.taskDataMap[taskType].isLoading = false;
                }
            }
        });

        // 如果用户直接访问任务详情页面，清空所有未读状态
        this.eventService.subscribe(EventServiceKeyE.TASK_PAGE_LOAD, () => {
            this.setAllTasksRead();
        });
    }

    /**
     * 初始化轮询
     */
    createTasksPolling(): {
        destroyer: () => void;
        immediate: () => void;
    } {
        const allPooling = [];

        for (const taskType of Object.values(TaskPanelStatus)) {
            const tasksRestJob = this.taskService.batchGetTaskRestJob(this.panelTaskStatusQueryMap[taskType]);
            const polling = this.taskService.pollData([tasksRestJob], this.pollingPeriod);

            this.pollingMap[taskType] = polling;
            allPooling.push(polling);

            polling.immediate();
            polling.subscribe(({ data }: { data: TaskData[] }) => {
                this.updateTaskData(data, taskType);
            });

        }

        return {
            destroyer: (): void => {
                for (const polling of allPooling) {
                    this.taskService.removePolling(polling);
                }
            }, immediate: (): void => {
                for (const polling of allPooling) {
                    polling.immediate();
                }
            },
        };
    }

    /**
     * 根据任务类型更新任务数据
     * @param taskData 任务数据
     * @param taskType 任务类型
     */
    updateTaskData(taskData: TaskData[], taskType: TaskPanelStatus): void {
        this.taskDataMap[taskType] = {
            tasks: [...taskData],
            noData: taskData ? taskData.length === 0 : true,
            hasUnreadTasks: this.isUnreadTasksExisted(taskData, taskType),
            isLoading: false,
        };
        this.updateAllTasksReadStatus();
    }

    /**
     * 显示面板时的回调函数
     */
    onPanelShow(): void {
        this.pollingImmediate();
        this.setTaskReadStatus(this.currentTaskType, false);
        this.setLastVisitTime(this.currentTaskType);
    }

    /**
     * 根据任务类型设置已读状态
     */
    setTaskReadStatus(taskTypes: TaskPanelStatus | TaskPanelStatus[], value: TaskModel['hasUnreadTasks']): void {
        if (Array.isArray(taskTypes)) {
            for (const taskType of taskTypes) {
                this.taskDataMap[taskType].hasUnreadTasks = value;
            }
        } else {
            this.taskDataMap[taskTypes].hasUnreadTasks = value;
        }
    }

    /**
     * 关闭面板时的回调函数
     */
    onPanelHide(): void {
        this.setLastVisitTime(this.currentTaskType);
        this.updateAllTasksReadStatus();
    }

    /**
     * tab切换事件回调
     * @param taskType
     */
    tabSwitcher(taskType: TaskPanelStatus): void {
        // 记录上一个tab对应任务类型最后访问时间
        this.setLastVisitTime(this.currentTaskType);
        // 记录当前tab对应任务类型的最新访问时间
        this.setLastVisitTime(taskType);
        // 对新tab对应任务类型设置为已访问状态
        this.taskDataMap[taskType].hasUnreadTasks = false;
        // tab切换
        this.currentTaskType = taskType;
        // 每次切换后更新
        this.updateAllTasksReadStatus();
    }

    /**
     * 更新【所有任务】已读状态，三种类型任务都被查看了才消除红点
     */
    updateAllTasksReadStatus(): void {
        this.hasAllTasksRead = Object.values(this.taskDataMap).every(({ hasUnreadTasks }) => !hasUnreadTasks);
    }

    /**
     * 清空所有未读状态
     */
    setAllTasksRead(): void {
        this.setTaskReadStatus(this.allTasks, false);
        this.updateAllTasksReadStatus();

        Object.keys(TaskPanelStatus).forEach((taskType: string) => {
            this.setLastVisitTime(TaskPanelStatus[taskType]);
        });
    }

    /**
     * tab动态样式
     * @returns tab class样式
     */
    tabStyle(taskType: TaskPanelStatus, currentTaskType: TaskPanelStatus): { selected?: boolean; unselected?: boolean } {
        if (currentTaskType === taskType) {
            return { selected: true };
        }
        return { unselected: true };
    }

    /**
     * icon是否显示为进度条
     * @param task
     * @returns boolean
     */
    shouldShowProgress(task: TaskData): boolean {
        return task.taskStatus === this.taskStatus.running || task.taskStatus === this.taskStatus.rollbacking;
    }

    /**
     * 进度数值计算
     *
     * @param task
     * @returns number 进度值，比如60%会返回60
     */
    calProgress(task: TaskData): number | undefined {
        const description = task.description;
        if (description && Number.isFinite(description.currentStepIndex) && Number.isFinite(description.stepCount)) {
            task.progress = Math.floor(description.currentStepIndex / description.stepCount * 100);
        } else {
            task.progress = undefined;
        }
        return task.progress;
    }

    /**
     * 点击 View All Tasks > 之后的回调
     */
    viewAllTasks(): void {
        if (this.tooltip) {
            this.tooltip.hide();
        }
        // 如果点击了【查看所有任务】，所有未读状态清空
        this.setAllTasksRead();
        if (SWITCH.IS_SIMPLE_MODE) {
            // 关闭上一个侧滑框后打开新的侧滑窗
            this.lazyLoadService.removeComponent(this.taskSComponent);
            this.lazyLoadService.destroySelf(this.taskSComponent);
            this.taskSComponent = this.lazyLoadService.appendComponent(TaskSComponent);
        } else {
            this.router.navigate(['/monitor/task']);
        }
    }

    /**
     * 点击单项任务后跳转任务详情页
     * @param task
     */
    viewDetail(task: TaskData): void {
        if (this.tooltip) {
            this.tooltip.hide();
        }

        if (SWITCH.IS_SIMPLE_MODE) {
            // 关闭上一个侧滑框后打开新的侧滑窗
            this.lazyLoadService.removeComponent(this.taskSComponent);
            this.lazyLoadService.destroySelf(this.taskSComponent);
            this.taskSComponent = this.lazyLoadService.appendComponent(TaskSComponent, {
                taskId: task.id,
            });
        } else {
            // 选中任务对应的租户，如果没有vstoreid默认选中全部
            const vstoreId = task.vstoreId ?? this.allVstore;
            this.sessionStorageService.set('vstoreId', vstoreId);

            const chosenTheSameTask = this.route.snapshot?.queryParams?.actionParams === task.id &&
                this.route.snapshot?.queryParams?.action === 'restoreRouteState';

            if (chosenTheSameTask) {
                // 如果点击了路由记录的同一个任务，不用传参(任务中心组件已记录)，但不改变url，允许跳转到同一个任务
                this.router.navigate(['/monitor/task'], { skipLocationChange: true });
            } else {
                this.router.navigate(['/monitor/task'], {
                    queryParams: {
                        action: 'restoreRouteState',
                        actionParams: task.id,
                    },
                });
            }
        }
    }

    /**
     * 根据任务类型动态渲染不同的icon
     * @param task 任务数据
     * @returns
     */
    getStateClass(task: TaskData): string {
        switch (task.taskStatus) {
            case this.taskStatus.waiting:
                return 'waiting';
            case this.taskStatus.running:
            case this.taskStatus.rollbacking:
                return '';
            case this.taskStatus.success:
                return 'succeed';
            case this.taskStatus.failed:
            case this.taskStatus.rollbackFailed:
                return 'error';
            case this.taskStatus.rollbackSuccess:
            case this.taskStatus.particalFailed:
                return 'partitial-failed';
            default:
                return '';
        }
    }

    /**
     * 对象名称转义格式化
     * @param objName 对象名
     * @returns 格式化后的对象名称
     */
    replaceBlock(objName: TaskData['objName']): string {
        return this.utilService.encodeHTML(objName).replace(/ /g, '&nbsp;') || UNPLACEHOLDER;
    }

    /**
     * 获取用户对特定状态任务上一次的访问时间
     * @param status 任务状态
     * @returns time 时间，单位：秒
     */
    getLastVisitTime(status: TaskPanelStatus): number | undefined {
        return this.localStorageService.get(status);
    }

    /**
     * 设置用户对特定状态任务上一次的访问时间
     * @param status 任务状态
     */
    setLastVisitTime(status: TaskPanelStatus): void {
        const curTime = Math.ceil(Date.now() / 1000);
        this.localStorageService.set(status, curTime);
    }

    /**
     * 是否有未读任务
     * @param data
     * @param status
     * @returns
     */
    isUnreadTasksExisted(data: TaskData[], status: TaskPanelStatus): boolean {
        // 没有数据直接返回
        if (!data) {
            return false;
        }
        // 如果任务tab正显示着就不更新已读状态
        if (this.tooltipVisible && this.currentTaskType === status) {
            return false;
        }

        const times = data.map((item) => Number(item.endTime));
        const targetStatusTaskReadTime = this.getLastVisitTime(status) ?? 0;
        const unreadTasks = times.filter((time) => time > targetStatusTaskReadTime);

        return unreadTasks.length > 0;
    }
}
